import { useEffect, useState } from "react";
import { listSkus } from "./api";

let cache = null;
let pending = null;
const listeners = new Set();

function notify() {
    listeners.forEach((fn) => fn(cache));
}

export function getCachedSkus() {
    return cache;
}

export async function loadSkus({ force = false, signal } = {}) {
    if (!force && cache) return cache;
    if (pending) return pending;
    pending = listSkus({ signal })
        .then((res) => {
            cache = res?.skus ?? [];
            pending = null;
            notify();
            return cache;
        })
        .catch((err) => {
            pending = null;
            throw err;
        });
    return pending;
}

export function invalidateSkus() {
    cache = null;
    notify();
}

export function setCachedSkus(next) {
    cache = next;
    notify();
}

export function subscribeSkus(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}

export function useSkus({ auto = true } = {}) {
    const [skus, setSkus] = useState(cache);
    const [loading, setLoading] = useState(cache === null && auto);
    const [error, setError] = useState(null);

    useEffect(() => subscribeSkus(setSkus), []);

    useEffect(() => {
        if (!auto) return;
        if (cache !== null) return;
        setLoading(true);
        setError(null);
        loadSkus()
            .catch((err) => setError(err))
            .finally(() => setLoading(false));
    }, [auto]);

    const refresh = async () => {
        setLoading(true);
        setError(null);
        try {
            return await loadSkus({ force: true });
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    return { skus: skus ?? [], loading, error, refresh };
}
